export interface FrameFrequencyBands {
  bass: ArrayLike<number>;
  mid: ArrayLike<number>;
  high: ArrayLike<number>;
}

const channels = ["bass", "mid", "high"] as const;
const unit = (value: number) => Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;
const bank = (size: number) => ({ bass: new Float32Array(size), mid: new Float32Array(size), high: new Float32Array(size) });

/** Zero at the current front, increasing toward the back after every wrap. */
export function frameFrequencyMix(positions: readonly number[], exponent = 1.65) {
  const front = Math.max(...positions), back = Math.min(...positions);
  return positions.map(z => front > back ? Math.pow(unit((front - z) / (front - back)), exponent) : 0);
}

export function mixFrameFrequencyBands(bands: FrameFrequencyBands, positions: readonly number[]) {
  const mid = frameFrequencyMix(positions, 0.75);
  const high = frameFrequencyMix(positions);
  return Array.from({ length: bands.bass.length }, (_, edge) => {
    const frame = Math.floor(edge / 4);
    const midGain = mid[frame] ?? 0, highGain = high[frame] ?? 0;
    return unit((bands.bass[edge] ?? 0) * (1 - midGain) +
      (bands.mid[edge] ?? 0) * midGain + (bands.high[edge] ?? 0) * highGain);
  });
}

/** Filter each frequency class separately; depth is applied to the result, not its history. */
export class FrameFrequencyResponse {
  private levels;
  private previous;
  private sparks;
  private rangeKey: string | null = null;

  constructor(size: number) {
    this.levels = bank(size);
    this.previous = bank(size);
    this.sparks = bank(size);
  }

  step(bands: FrameFrequencyBands, rangeKey: string, dt: number, positions: readonly number[]) {
    const reset = this.rangeKey !== null && this.rangeKey !== rangeKey;
    if (reset) for (const channel of channels) {
      this.levels[channel].fill(0);
      this.previous[channel].fill(0);
      this.sparks[channel].fill(0);
    }
    this.rangeKey = rangeKey;
    for (const channel of channels) for (let edge = 0; edge < this.levels[channel].length; edge++) {
      const input = unit(bands[channel][edge] ?? 0);
      const target = Math.pow(unit((input - 0.28) / 0.52), 2.2);
      const previous = this.levels[channel][edge]!;
      const tau = target > previous ? 0.018 : 0.22;
      this.levels[channel][edge] = target + (previous - target) * Math.exp(-dt / tau);
      const rise = Math.max(0, input - this.previous[channel][edge]!) / (dt * 60);
      this.previous[channel][edge] = input;
      this.sparks[channel][edge] = Math.max(this.sparks[channel][edge]! * Math.exp(-dt / 0.12),
        rise > 0.012 ? unit(rise * 16) : 0);
    }
    return {
      edges: mixFrameFrequencyBands(this.levels, positions),
      sparks: mixFrameFrequencyBands(this.sparks, positions),
      reset,
    };
  }
}
